import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { api } from "../Assets/api/api";

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children, user }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    
    const fetchNotifications = async () => {
      try {
        setIsLoadingNotifications(true);
        const response = await api.get(`/api/in-app-notifications/${user.slug}`);
        const notifications = response.data.results;
        
        // Only update state if component is still mounted
        if (isMounted) {
          setNotifications(notifications);
          setUnreadCount(notifications.filter((n) => !n.isRead).length);
        }
      } catch (error) {
        if (isMounted) {
          toast.error("Failed to fetch notifications");
          console.error("Error fetching notifications:", error);
        }
      } finally {
        if (isMounted) {
          setIsLoadingNotifications(false);
        }
      }
    };
    
    // Only fetch notifications for Admin and SuperAdmin roles
    // Teachers and Students don't have access to notification UI
    if (
      user?.slug &&
      user?.role &&
      ["Admin", "SuperAdmin"].includes(user.role)
    ) {
      fetchNotifications();
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [user?.slug, user?.role]);

  const getNotifications = async () => {
    try {
      setIsLoadingNotifications(true);
      const response = await api.get(`/api/in-app-notifications/${user.slug}`);
      const notifications = response.data.results;
      setNotifications(notifications);
      setUnreadCount(notifications.filter((n) => !n.isRead).length);
    } catch (error) {
      toast.error("Failed to fetch notifications");
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      await api.patch(
        `/api/in-app-notifications/${notificationId}/mark-read${user.slug}`,
        {
          isRead: true,
          schoolId: user.schoolId,
        }
      );
    } catch (error) {
      toast.error("Failed to mark notification as read");
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, isRead: true }))
      );
      setUnreadCount(0);
      await api.patch(`/api/in-app-notifications/mark-all-read/${user.slug}`);
    } catch (error) {
      toast.error("Failed to mark all notifications as read");
      console.error("Error marking all notifications as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const notificationToDelete = notifications.find(
        (n) => n.id === notificationId
      );
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== notificationId)
      );
      if (notificationToDelete && !notificationToDelete.isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
      await api.delete(
        `/api/in-app-notifications/${notificationId}/delete${user.slug}`
      );
    } catch (error) {
      toast.error("Failed to delete notification");
      console.error("Error deleting notification:", error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      setNotifications([]);
      setUnreadCount(0);
      await api.delete(`/api/in-app-notifications/clear-all${user.slug}`);
    } catch (error) {
      toast.error("Failed to clear notifications");
      console.error("Error clearing notifications:", error);
    }
  };

  const getNotificationHandler = (type) => {
    const handlers = {
      "shared-spinWheel-added": (notification) => {
        // Handle spin wheel notification
        return {
          ...notification,
          onClick: () => {
            // Navigate to spin wheel
            // window.location.href = `/spin-wheel/${notification.metadata.spinWheelName}`;
          },
        };
      },
      "shared-collection-added": (notification) => {
        // Handle collection notification
        return {
          ...notification,
          onClick: () => {
            // Navigate to collection
            // window.location.href = `/collections/${notification.metadata.collectionId}`;
          },
        };
      },
      "request-approved": (notification) => {
        // Handle approved request
        return {
          ...notification,
          onClick: () => {
            // Navigate to trust network
            // window.location.href = '/trusted-network';
          },
        };
      },
      "request-rejected": (notification) => {
        // Handle rejected request
        return {
          ...notification,
          onClick: () => {
            // Navigate to trust network
            // window.location.href = '/trusted-network';
          },
        };
      },
      "request-sent": (notification) => {
        // Handle sent request
        return {
          ...notification,
          onClick: () => {
            // Navigate to trust network
            // window.location.href = '/trusted-network';
          },
        };
      },
    };

    return handlers[type] || ((notification) => notification);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoadingNotifications,
        getNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        getNotificationHandler,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
